/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/common/app';
import { slug } from '..';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import AchievementLevelsState from '../states/AchievementLevelsState';

// Make translation calls shorter
const t = (...args) => app.translator.trans(...args);
const prfx = `${slug}.lib.auto_moderator.achievement_level_selector`;

export default class AchievementLevelSelector extends Component {
	oninit(vnode) {
		super.oninit(vnode);

		this.loaded = false;

		this.state = new AchievementLevelsState();

		// Load achievement levels
		this.state.loadAll().then(() => {
			this.loaded = true;
			m.redraw();
		});
	}

	get achievement_levels() {
		let achievementLevels = app.store.all('achievement_levels');

		if (this.attrs.user) {
			// Filter out achievement levels that the user already has
			achievementLevels = achievementLevels.filter((achievementLevel) => {
				if (!achievementLevel?.achievement()) {
					// Should never happen, but just in case, we filter this out
					return false;
				}
				const achievementId = achievementLevel?.achievement()?.id();
				const userAchievements = this.attrs.user?.userAchievements() || [];

				return !userAchievements.some((userAchievement) => {
					return userAchievement?.achievement().id() === achievementId;
				});
			});
		}

		return achievementLevels;
	}

	view() {
		const buttonClassName = `Button ${this.attrs.user ? '' : 'Button--danger'}`;

		if (!this.loaded) {
			return (
				<div className="Form-group">
					<label>{this.attrs.label}</label>

					<Button className={buttonClassName} style={{ float: 'none' }} loading>
						{t(`${prfx}.loading`)}
					</Button>
				</div>
			);
		}

		const achievement_level = app.store.getById('achievement_levels', this.attrs.value);
		const label = achievement_level ? achievement_level.name() : t(`${prfx}.placeholder`);
		return (
			<div className="Form-group">
				<label>{this.attrs.label}</label>

				{this.attrs.disabled ? (
					<div className={buttonClassName}>{label}</div>
				) : (
					<Dropdown label={label} disabled={this.attrs.disabled} buttonClassName={buttonClassName}>
						{this.achievement_levels.map((_achievement_level) => (
							<Button
								active={achievement_level && achievement_level.id() === _achievement_level.id()}
								disabled={achievement_level && achievement_level.id() === _achievement_level.id()}
								icon={_achievement_level.icon()}
								onclick={() => this.attrs.onchange(_achievement_level.id())}
							>
								{_achievement_level.name()}
							</Button>
						))}
					</Dropdown>
				)}
			</div>
		);
	}
}
